import { Console } from "../interface/console.interface";
import { Enum } from "../support/enum.support";
import { LoggingLevel } from "./logging-level.enum";

    /**
     * @description
     *  Un logger inmutable con diferentes niveles de detalle.
     */

export class Logger {

    constructor(
        private name: string,
        private level: LoggingLevel) {
    }

    public debug(...messages: any[]): void {
        this.log(console.debug, LoggingLevel.DEBUG, "#1187fe", "black", ...messages);
    }

    public error(...messages: any[]): void {
        this.log(console.error, LoggingLevel.ERROR, "#de0000", "black", ...messages);
    }

    public fatal(...messages: any[]): void {
        this.log(console.error, LoggingLevel.FATAL, "#77ff33", "black", ...messages);
    }

    public info(...messages: any[]): void {
        this.log(console.info, LoggingLevel.INFORMATION, "#f3f3f3", "black", ...messages);
    }

    public trace(...messages: any[]): void {
        this.log(console.trace, LoggingLevel.TRACE, "#973aff", "black", ...messages);
    }

    public warn(...messages: any[]): void {
        this.log(console.warn, LoggingLevel.WARNING, "#ffd100", "black", ...messages);
    }

    /**
     * @description
     *  Método genérico de bajo nivel de logging. Muestra un mensaje, con el
     *  'timestamp' actual en formato ISO-8601 simplificado extendido, el
     *  nivel de logging y el nombre del logger, utilizando la consola
     *  especificada. Si un mensaje es de tipo función, entonces se evalúa en
	 *	caso de ser necesario, y su resultado se imprime en la consola, lo que
	 *	permite evaluar de forma lazy los mensajes más complejos o extensos.
     *
     * @param console
     *  Consola utilizada para imprimir en pantalla (e.g., 'console.log').
     * @param level
     *  El nivel de logging. Si el nivel de logging es superior al nivel
     *  establecido para el entorno actual, el mensaje es descartado.
     * @param color
     *  El color de la fuente del mensaje de encabezado.
     * @param background
     *  El color del fondo del mensaje de encabezado.
     * @param messages
     *  Los mensajes a imprimir en la consola especificada.
     */
    private log(console: Console, level: LoggingLevel,
        color: string, background: string, ...messages: any[]): void {
        if (level <= this.level) {
			const resolvedMessages = messages.map(message => {
				if (typeof message === "function") {
					return message();
				}
				else {
					return message;
				}
			});
            const levelName: string = Enum.toString(LoggingLevel, level);
            console(
                `%c[${Logger.now()}][${Logger.trim(levelName)}][${this.name}]`,
                `color: ${color}; background-color: ${background}`,
                ...resolvedMessages
            );
        }
    }

    /**
     * @description
     *  Devuelve el 'timestamp' actual como una cadena de caracteres en
     *  formato ISO 8601 simplificado extendido, el cual es de la forma:
     *
     *      "YYYY-MM-DDTHH:mm:ss.sssZ"
     *
     * @returns
     *  El 'timestamp' actual.
     */
    private static now(): string {
        return new Date().toISOString();
    }

    /**
     * @description
     *  Reduce el tamaño de una cadena para que no ocupe más de 5 caracteres
     *  de largo. Como está diseñado para el nombre del nivel de logging, si
     *  el mismo es más largo que 5, entonces se toman los primeros 4; sino se
     *  toma el nombre completo.
     *
     * @param levelName 
     *  El nombre del nivel de logging.
     *
     * @returns
     *  El nombre reducido, completado con espacios hasta 5 caracteres.
     */
    private static trim(levelName: string): string {
        if (5 < levelName.length) {
            return levelName.substring(0, 4).padEnd(5);
        }
        else {
            return levelName.padEnd(5);
        }
    }
}
